import { Link } from 'react-router-dom';
import { GripVertical, Pencil, Plus } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useProjects } from '../stores/projects.js';
import { useUi } from '../stores/ui.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { Button } from '../ui/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { teinte } from '../lib/teinte.js';
import type { Project } from '../api/projects.js';

/**
 * Gestion des projets — atteignable depuis Settings uniquement (ADR 0007) : c'est de la
 * maintenance rare, pas une destination quotidienne. La page sert aussi d'index vers les
 * projets qui n'ont aucune tâche affichée dans Today ou Board.
 *
 * L'ordre défini ici pilote l'ordre des sections dans Today et Board — c'est la raison
 * pour laquelle le drag-reorder a suivi le déménagement au lieu d'être supprimé.
 */
export function Projects(): React.ReactElement {
  // Pas de chargement ici : AppShell appelle loadProjects() au montage, pour toutes
  // les routes, et create / update / reorder mettent le store à jour eux-mêmes.
  const projects = useProjects((s) => s.projects);
  const reorder = useProjects((s) => s.reorder);
  const openProjectForm = useUi((s) => s.openProjectForm);
  const isMobile = useIsMobile();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: isMobile ? { delay: 250, tolerance: 5 } : { distance: 5 },
    })
  );

  function onDragEnd(e: DragEndEvent): void {
    if (!e.over || e.active.id === e.over.id) return;
    const oldIdx = projects.findIndex((p) => p.projectId === e.active.id);
    const newIdx = projects.findIndex((p) => p.projectId === e.over!.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(projects, oldIdx, newIdx);
    reorder(reordered.map((p) => p.projectId));
  }

  return (
    <div className="max-w-2xl mx-auto w-full px-4 md:px-6 py-4 md:py-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="hidden md:block text-2xl">Projets</h1>
        <Button onClick={() => openProjectForm()} className="md:ml-auto">
          <Plus size={16} aria-hidden />
          Nouveau projet
        </Button>
      </div>

      <p className="discret">
        L&apos;ordre ci-dessous est celui des sections dans Today et Upcoming.
      </p>

      {projects.length === 0 ? (
        <EmptyState>Aucun projet — crée le premier pour ranger tes tâches.</EmptyState>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext
            items={projects.map((p) => p.projectId)}
            strategy={verticalListSortingStrategy}
          >
            <div className="liste liste-aeree">
              {projects.map((p) => (
                <ProjectRow
                  key={p.projectId}
                  project={p}
                  onEdit={openProjectForm}
                  mobile={isMobile}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

function ProjectRow({
  project,
  onEdit,
  mobile,
}: {
  project: Project;
  onEdit: (pid: string) => void;
  mobile: boolean;
}): React.ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: project.projectId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="ligne ligne-bordee ligne-cliquable gap-1 px-1 py-0">
      <Button
        {...listeners}
        {...attributes}
        variant="quiet"
        size={mobile ? 'icon' : 'icon-small'}
        aria-label={`Reorder ${project.name}`}
        className="touch-none cursor-grab active:cursor-grabbing"
      >
        <GripVertical size={16} aria-hidden />
      </Button>
      <Link
        to={`/p/${project.projectId}`}
        className="flex items-center gap-3 flex-1 min-w-0 self-stretch no-underline text-inherit"
      >
        <span className="point point-grand" style={teinte(project.color)} />
        <span className="truncate">{project.name}</span>
      </Link>
      <Button
        variant="quiet"
        size="icon"
        onClick={() => onEdit(project.projectId)}
        aria-label={`Edit ${project.name}`}
      >
        <Pencil size={16} aria-hidden />
      </Button>
    </div>
  );
}
